import React, { useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import TemplateDetailView from './TemplateDetail.view';
import { fetchAmendmentDetail, deleteAmendment, buildPagingQuery } from '../AmendContract.service';
import { convertToDate, errorToastAction } from 'utils';
import errorCb from 'Common/Utils/errorCb';

const actions = {
    RESET: 'RESET',
    SUCCESS: 'SUCCESS',
    FAILURE: 'FAILURE',
    LOADED: 'LOADED',
    DELETE_CONFIRMED: 'DELETE_CONFIRMED'
};
const page = buildPagingQuery.bind(null, 'asc', 'amendmentNumber', true);

function TemplateDetail({
    template,
    type,
    onBack,
    onClose
}) {
    const globalDispatch = useDispatch();
    const [{
        dataSource,
        year,
        typeCode,
        typeDescription,
        templateName,
        valid,
        lastAmendment,
        showAdd,
        showDelete,
        showDeletePrompt,
        showEdit,
        exportDataSource,
        deleteOutcome,
        deleteMessage
    }, dispatch] = useReducer(reducer, { template, type, globalDispatch }, initState);

    const handleAddClick = useCallback(() => {
        const amendment = {
            markingTeamId: template.markingTeamId,
            type: type.type,
            amendmentNumber: lastAmendment ? lastAmendment.amendmentNumber + 1 : 1,
            amendmentDate: new Date(),
            amendmentReasonIds: [],
            qualificationYear: template.qualificationYear
        };
        amendment.amendmentDate.setHours(0, 0, 0, 0);

        onClose('edit_amendment', {
            amendment,
            mode: 'add',
            onBack: () => onClose('amend_contract_detail', { template, type, onBack })
        });
    }, [onClose, template, type, lastAmendment, onBack]);

    const handleEditClick = useCallback(() => {
        const amendment = {
            markingTeamId: template.markingTeamId,
            type: type.type,
            amendmentNumber: lastAmendment.amendmentNumber,
            amendmentDate: lastAmendment.amendmentDate,
            amendmentReasonIds: lastAmendment.amendmentReasonIds,
            qualificationYear: template.qualificationYear
        };
        onClose('edit_amendment', {
            amendment: amendment,
            mode: 'edit',
            onBack: () => onClose('amend_contract_detail', { template, type, onBack })
        });
    }, [onBack, onClose, lastAmendment, template, type]);

    const handleDataLoaded = useCallback((data) => {
        dispatch(loaded(data));
    }, [dispatch]);

    const handleDeletePromptChange = useCallback((e) => {
        e.persist();
        const checked = e.target.checked;
        dispatch(deleteConfirmed(checked));
    }, [dispatch]);

    const handleDeleteClick = useCallback(async () => {
        const errorCallback = message => dispatch(failure('Error deleting amendment. ' + message));
        if (await deleteAmendment(lastAmendment.amendmentIds, errorCallback)) {
            dispatch(success());
            dispatch(reset(template, type, globalDispatch));
        }
    }, [lastAmendment, template, type, dispatch, globalDispatch]);

    useEffect(() => {
        if (!valid) {
            globalDispatch(errorToastAction('Selected template details were not supplied to the Amend Contract page.'));
            onClose('amend_contract');
        }
    }, [valid, onClose, globalDispatch]);

    const infoMessage = lastAmendment?.amendmentNumber >= 6 ? 'Maximum number of amendments reached.' : null;

    return !valid ? null : (
        <TemplateDetailView
            amendmentNumber={(lastAmendment && lastAmendment.amendmentNumber) || 0}
            dataSource={dataSource}
            deleteOutcome={deleteOutcome}
            deleteMessage={deleteMessage}
            exportDataSource={exportDataSource}
            infoMessage={infoMessage}
            showAdd={showAdd}
            showData={!!dataSource}
            showDelete={showDelete}
            showDeletePrompt={showDeletePrompt}
            showEdit={showEdit}
            showExport={!!exportDataSource}
            templateName={templateName}
            type={typeCode}
            typeDescription={typeDescription}
            year={year}
            onAddClick={handleAddClick}
            onEditClick={handleEditClick}
            onBack={onBack}
            onDataLoaded={handleDataLoaded}
            onDeleteClick={handleDeleteClick}
            onDeletePromptChange={handleDeletePromptChange}
        />
    );
}

function getExportData(data, template, type) {
    const results = [];
    for (let rowNum = 0; rowNum < data.length; rowNum++) {
        const row = data[rowNum];
        const flatRow = {
            ...row,
            year: template.qualificationYear,
            type: type.type,
            typeDescription: type.description,
            template: template.templateName,
        };
        delete flatRow.amendmentIds;
        delete flatRow.amendmentReasonIds;
        delete flatRow.amendmentReasons;

        if (row.amendmentIds && row.amendmentIds.length > 0) {
            for (let i = 0; i < row.amendmentIds.length; i++) {
                const resultRow = {
                    ...flatRow,
                    amendmentId: row.amendmentIds[i],
                    amendmentReasonId: row.amendmentReasonIds.length > i ? row.amendmentReasonIds[i] : null,
                    amendmentReason: row.amendmentReasons.length > i ? row.amendmentReasons[i] : null,
                }

                results.push(resultRow);
            }
        } else {
            results.push(flatRow);
        }
    }

    return results;
}

function initState(initialValue) {
    const { template, type, globalDispatch } = initialValue;
    const year = template && String(template.qualificationYear);
    const markingTeamId = template && template.markingTeamId;
    const typeCode = type && type.type;
    const typeDescription = type && type.description;
    const valid = !!(year && typeCode && markingTeamId);
    const dataSource = valid
        ? () => fetchAmendmentDetail(year, markingTeamId, typeCode, page(),
            errorCb([], 'Error fetching amendments.', globalDispatch))
        : null;

    const newState = {
        globalDispatch,
        valid,
        typeCode,
        typeDescription,
        template,
        templateName: template.templateName,
        type,
        year,
        dataSource,
        lastAmendment: null,
        showAdd: false,
        showDelete: false,
        showDeletePrompt: false,
        showEdit: false,
        exportDataSource: null,
        deleteOutcome: null,
        deleteMessage: null
    };
    return newState;
}

function reducer(state, action) {
    switch (action.type) {
        case actions.RESET:
            const newState = initState(action.payload);
            newState.deleteMessage = state.deleteMessage;
            newState.deleteOutcome = state.deleteOutcome;
            return newState;

        case actions.SUCCESS:
            return {
                ...state,
                showAdd: false,
                showEdit: false,
                showDeletePrompt: false,
                showDelete: false,
                lastAmendment: null,
                exportDataSource: null,
                deleteOutcome: true,
                deleteMessage: `Amendment ${state.lastAmendment.amendmentNumber} deleted.`
            };

        case actions.FAILURE:
            return {
                ...state,
                deleteOutcome: false,
                deleteMessage: action.payload,
            };

        case actions.LOADED:
            const data = action.payload;
            const amendmentCount = data.length;
            const lastNumber = data.reduce((max, item) => Math.max(max, item.amendmentNumber), -1);
            const last = data.find(item => item.amendmentNumber === lastNumber);
            const lastDate = last && convertToDate(last.amendmentDate);
            const today = new Date().setHours(0, 0, 0, 0);
            const lastIsActive = lastDate && !isNaN(lastDate) && lastDate < today;

            return {
                ...state,
                showAdd: amendmentCount === 0 || (amendmentCount < 6 && lastIsActive),
                showEdit: last && !lastIsActive,
                showDeletePrompt: amendmentCount > 0 && !lastIsActive,
                showDelete: false,
                lastAmendment: last,
                exportDataSource: () => getExportData(data, state.template, state.type)
            };

        case actions.DELETE_CONFIRMED:
            const checked = action.payload;
            return {
                ...state,
                showDeletePrompt: !checked,
                showDelete: checked,
            };

        default:
            return state;
    }
}

function reset(template, type, globalDispatch) {
    return { type: actions.RESET, payload: { template, type, globalDispatch } };
}

function success() {
    return { type: actions.SUCCESS };
}

function failure(message) {
    return { type: actions.FAILURE, payload: message };
}

function deleteConfirmed(checked) {
    return { type: actions.DELETE_CONFIRMED, payload: checked };
}

function loaded(data) {
    return { type: actions.LOADED, payload: data };
}

TemplateDetail.propTypes = {
    template: PropTypes.object,
    type: PropTypes.object,
    onBack: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

export default TemplateDetail;